import { Box, Input, Modal, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { formatTimestampToDate, extractTextContent } from '../../common/utils';
import React from 'react';

interface SearchModalProps {
  openSearchModal: boolean;
  setOpenSearchModal: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  chatsData: any[];
  onSelectChat: (chatId: string, fieldType?: string) => void;
  isLoadingMore?: boolean;
  hasReachedEnd?: boolean;
  onLoadMore?: () => void;
  onSearchChange?: (searchQuery: string) => void;
  onCategoryChange?: (cat: string) => void;
  setCategory?: (cat: string) => void;
  setSelectedElasticChat?: (chat: any) => void;
  setSessions?: (chat: any) => void;
}

const highlightText = (text: string, searchQuery: string): React.ReactNode => {
  if (!searchQuery || !text) {
    return text;
  }

  const query = searchQuery.trim();
  if (!query) {
    return text;
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const testRegex = new RegExp(`^${escapedQuery}$`, 'i');
        return testRegex.test(part) ? (
          <strong key={index}>{part}</strong>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        );
      })}
    </>
  );
};

const SearchModal = ({
  openSearchModal,
  setOpenSearchModal,
  searchQuery,
  setSearchQuery,
  chatsData,
  onSelectChat,
  isLoadingMore = false,
  hasReachedEnd = false,
  onLoadMore,
  onSearchChange,
  onCategoryChange = () => {},
  setCategory = () => {},
  setSelectedElasticChat = () => {},
  setSessions = () => {},
}: SearchModalProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Handle scroll detection for infinite scroll (similar to ChatListingSidebar)
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !onLoadMore || isLoadingMore || hasReachedEnd) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger load more when user is 200px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [onLoadMore, isLoadingMore, hasReachedEnd]);

  // Debounce search query changes and notify parent (similar to ChatListingSidebar)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearchChange]);
  return (
    <Modal
      open={openSearchModal}
      onClose={() => setOpenSearchModal(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '58vw',
          height: '58vh',
          backgroundColor: 'white',
          borderRadius: '10px',
          pl: 2,
          pr: 0.25,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
          <SearchIcon sx={{ color: '#666666' }} />
          <Input
            disableUnderline
            placeholder="Search Chats"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              setTimeout(() => {
                setSearchQuery(pastedText);
              }, 0);
            }}
            sx={{ width: '100%' }}
          />
        </Box>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            flex: 1,
            maxHeight: 'calc(45vh - 130px)',
            minHeight: '45vh',
            px: 1,
            py: 1,
            '&::-webkit-scrollbar': {
              width: '9px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#B8B8B8',
              borderRadius: '4px',
              minHeight: 143,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#9ca3af',
            },
          }}
        >
          {!isLoadingMore && chatsData.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#737373',
                  fontFamily: 'Poppins',
                }}
              >
                No chats found
              </Typography>
            </Box>
          ) : (
            <>
              {chatsData.map((chat: any) => {
                return (
                  <Box
                    key={chat.id}
                    onClick={() => {
                      setCategory('All');
                      onCategoryChange('All');
                      setSelectedElasticChat(chat);
                      setSessions((prev: any[]) => [
                        chat,
                        ...prev.filter((s) => s.conversation_id !== chat.conversation_id),
                      ]);
                      onSelectChat(chat?.conversation_id, chat?.field_type || '');
                      setOpenSearchModal(false);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#F4F7FF',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src="/images/chat-bubble.png"
                        alt={chat.name}
                        height={16}
                        width={16}
                        style={{ alignSelf: 'start', marginTop: 3 }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{ fontSize: '14px', fontWeight: 500, color: '#737373' }}
                        >
                          {chat.session_name || 'Untitled Session'}
                        </Typography>
                        {chat.highlight && (
                          <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                              fontSize: '12px',
                              color: '#737373',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '38vw',
                            }}
                          >
                            {highlightText(extractTextContent(chat.highlight || ''), searchQuery)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '14px', color: '#737373', alignSelf: 'flex-start' }}
                    >
                      Updated on {formatTimestampToDate(chat.updated_at)}
                    </Typography>
                  </Box>
                );
              })}

              {/* Loading indicator or end message at the bottom */}
              {isLoadingMore && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px' }}>
                    Loading more sessions...
                  </Typography>
                </Box>
              )}
              {!isLoadingMore && hasReachedEnd && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}
                  >
                    No more sessions available
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default SearchModal;
